// kernel/useStudioDevelop.ts
import { useCallback, useRef, type RefObject } from 'react';
import type {
  KernelHandle,
  Layer,
  StudioDevelopSettings,
  StudioDevelopSession,
  UseStudioDevelopReturnType,
  Workspace,
} from '#types/types';
import { setProjectSelectedLayersId } from '#lib/project-runtime-layer-ui-state';
import {
  DEFAULT_STUDIO_DEVELOP_SETTINGS,
  STUDIO_DEVELOP_CONTROLS,
  STUDIO_DEVELOP_PARAM_KEY,
  areStudioDevelopSettingsDefault,
  areStudioDevelopSettingsEqual,
  applyStudioDevelopSettingsToImageData,
  normalizeStudioDevelopSettings,
} from '#lib/studio-develop';
import { cloneLayerSnapshot } from '#lib/studio-runtime-dto';

interface UseStudioDevelopProps {
  kernel: RefObject<KernelHandle | null>;
}

const DEVELOP_TARGET_PIXELS = 12_000_000;
const DEVELOP_PREVIEW_TARGET_PIXELS = 900_000;
const DEVELOP_PREVIEW_DEBOUNCE_MS = 180;

type StudioDevelopProgressChunk = {
  y: number;
  height: number;
  width: number;
  progress: number;
  data: Uint8ClampedArray<ArrayBuffer>;
};
type FilterBaselineState = {
  workspaceId: string;
  filterLayerId: string;
  workerSourceId: string;
  workerSourceCached: boolean;
  baseImageData: ImageData;
  previewBaseImageData: ImageData;
  frameRect: Workspace['frameRect'];
  workspaceSnapshotBefore: Pick<Workspace, 'layerIds' | 'versions' | 'activeVersionId' | 'validatedVersionId'>;
  selectedLayerIdsBefore: string[];
  sourceLayerSnapshots: Layer[];
  committedSettings: StudioDevelopSettings | null;
  committedImageData: ImageData | null;
};

/**
 * The Filter engine retains one baseline (source composite, committed settings and committed
 * pixels) for the workspace it is editing. That baseline is only valid for the panel session that
 * produced it: when that session is torn down, it must be released, or the next session inherits
 * a stale composite. True when the retained baseline belongs to the workspace being torn down.
 */
export const shouldReleaseStudioDevelopBaseline = (
  retainedBaselineWorkspaceId: string | null | undefined,
  teardownWorkspaceId: string
): boolean => (
  typeof retainedBaselineWorkspaceId === 'string' &&
  retainedBaselineWorkspaceId.length > 0 &&
  retainedBaselineWorkspaceId === teardownWorkspaceId
);

const cloneSettings = (settings: StudioDevelopSettings): StudioDevelopSettings => ({ ...settings });

const createFilterLayerId = (): string => `layer-filter-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const revokePreviewUrl = (url: string | null | undefined): void => {
  if (typeof URL === 'undefined' || !url || !url.startsWith('blob:')) return;
  URL.revokeObjectURL(url);
};

const revokeSessionUrls = (session: Pick<StudioDevelopSession, 'previewUrl' | 'applyPreviewUrl'> | null | undefined): void => {
  if (!session) return;
  revokePreviewUrl(session.previewUrl);
  revokePreviewUrl(session.applyPreviewUrl);
};

const getControlLabel = (key: keyof StudioDevelopSettings): string => (
  STUDIO_DEVELOP_CONTROLS.find((control) => control.key === key)?.label ?? String(key)
);

const cloneWorkspaceVersionSnapshot = (workspace: Workspace): Pick<Workspace, 'layerIds' | 'versions' | 'activeVersionId' | 'validatedVersionId'> => ({
  layerIds: [...(workspace.layerIds ?? [])],
  versions: (workspace.versions ?? []).map((version) => ({
    ...version,
    layerVisibility: Array.isArray(version.layerVisibility)
      ? version.layerVisibility.map((entry) => ({ ...entry }))
      : [],
  })),
  activeVersionId: workspace.activeVersionId ?? null,
  validatedVersionId: workspace.validatedVersionId ?? null,
});

const buildLayerParams = (settings: StudioDevelopSettings): Record<string, unknown> => ({
  [STUDIO_DEVELOP_PARAM_KEY]: {
    settings: cloneSettings(settings),
    appliedAt: new Date().toISOString(),
  },
});

const cloneImageData = (imageData: ImageData): ImageData => (
  new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height)
);

const createStudioDevelopWorker = (): Worker => (
  new Worker(new URL('./studioDevelopWebWorker.js', import.meta.url))
);

const createPreviewImageData = (imageData: ImageData, targetPixels: number): ImageData => {
  const sourcePixels = imageData.width * imageData.height;
  if (sourcePixels <= targetPixels) {
    return cloneImageData(imageData);
  }

  const scale = Math.sqrt(targetPixels / sourcePixels);
  const width = Math.max(1, Math.round(imageData.width * scale));
  const height = Math.max(1, Math.round(imageData.height * scale));
  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = imageData.width;
  sourceCanvas.height = imageData.height;
  const sourceContext = sourceCanvas.getContext('2d');
  const previewCanvas = document.createElement('canvas');
  previewCanvas.width = width;
  previewCanvas.height = height;
  const previewContext = previewCanvas.getContext('2d');
  if (!sourceContext || !previewContext) {
    return cloneImageData(imageData);
  }

  sourceContext.putImageData(imageData, 0, 0);
  previewContext.drawImage(sourceCanvas, 0, 0, width, height);
  return previewContext.getImageData(0, 0, width, height);
};

const resolveVisibleLayerIds = (kernelHandle: KernelHandle, workspace: Workspace): string[] => {
  return kernelHandle
    .getLayersInWorkspaceContext(workspace.id)
    .filter((layer) => layer.visible !== false)
    .map((layer) => layer.id);
};

const runStudioDevelopWorker = (
  imageData: ImageData,
  settings: StudioDevelopSettings,
  onProgressChunk?: (chunk: StudioDevelopProgressChunk) => void
): Promise<ImageData> => {
  if (typeof Worker === 'undefined') {
    const result = applyStudioDevelopSettingsToImageData(imageData, settings);
    onProgressChunk?.({
      y: 0,
      height: result.height,
      width: result.width,
      progress: 1,
      data: new Uint8ClampedArray(result.data),
    });
    return Promise.resolve(result);
  }

  return new Promise<ImageData>((resolve, reject) => {
    const messageId = `studio-develop-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const worker = createStudioDevelopWorker();
    const sourceData = new Uint8ClampedArray(imageData.data);

    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
    };

    const onMessage = (event: MessageEvent) => {
      const payload = event.data || {};
      if (payload.messageId !== messageId) return;
      if (payload.chunkBuffer && typeof onProgressChunk === 'function') {
        const width = typeof payload.width === 'number' ? payload.width : imageData.width;
        const chunkHeight = typeof payload.chunkHeight === 'number' ? payload.chunkHeight : 0;
        const y = typeof payload.y === 'number' ? payload.y : 0;
        if (chunkHeight > 0) {
          onProgressChunk({
            y,
            height: chunkHeight,
            width,
            progress: typeof payload.progress === 'number' ? payload.progress : 0,
            data: new Uint8ClampedArray(payload.chunkBuffer as ArrayBuffer),
          });
        }
        return;
      }
      cleanup();
      if (payload.error) {
        reject(new Error(payload.error));
        return;
      }
      const resultBuffer = payload.resultBuffer;
      const width = typeof payload.width === 'number' ? payload.width : imageData.width;
      const height = typeof payload.height === 'number' ? payload.height : imageData.height;
      if (!resultBuffer) {
        reject(new Error('Filter worker returned no image data.'));
        return;
      }
      resolve(new ImageData(new Uint8ClampedArray(resultBuffer), width, height));
    };

    const onError = (event: ErrorEvent) => {
      cleanup();
      reject(new Error(event.message || 'Filter worker failed.'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    try {
      worker.postMessage({
        type: 'studioDevelop',
        messageId,
        sourceBuffer: sourceData.buffer,
        width: imageData.width,
        height: imageData.height,
        settings,
        progressive: typeof onProgressChunk === 'function',
        chunkRows: Math.max(24, Math.ceil(imageData.height / 28)),
      }, [sourceData.buffer]);
    } catch (error) {
      cleanup();
      reject(error instanceof Error ? error : new Error('Failed to send image data to Filter worker.'));
    }
  }).catch((error) => {
    console.warn('[useStudioDevelop] Filter worker failed, falling back to main thread.', error);
    const result = applyStudioDevelopSettingsToImageData(imageData, settings);
    onProgressChunk?.({
      y: 0,
      height: result.height,
      width: result.width,
      progress: 1,
      data: new Uint8ClampedArray(result.data),
    });
    return result;
  });
};

const runCachedStudioDevelopWorker = (
  worker: Worker,
  sourceId: string,
  imageData: ImageData | null,
  width: number,
  height: number,
  settings: StudioDevelopSettings
): Promise<ImageData> => {
  return new Promise<ImageData>((resolve, reject) => {
    const messageId = `studio-develop-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const sourceData = imageData ? new Uint8ClampedArray(imageData.data) : null;

    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };

    const onMessage = (event: MessageEvent) => {
      const payload = event.data || {};
      if (payload.messageId !== messageId) return;
      cleanup();
      if (payload.error) {
        reject(new Error(payload.error));
        return;
      }
      const resultBuffer = payload.resultBuffer;
      const resultWidth = typeof payload.width === 'number' ? payload.width : width;
      const resultHeight = typeof payload.height === 'number' ? payload.height : height;
      if (!resultBuffer) {
        reject(new Error('Filter worker returned no image data.'));
        return;
      }
      resolve(new ImageData(new Uint8ClampedArray(resultBuffer), resultWidth, resultHeight));
    };

    const onError = (event: ErrorEvent) => {
      cleanup();
      reject(new Error(event.message || 'Filter worker failed.'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    try {
      const payload: Record<string, unknown> = {
        type: 'studioDevelop',
        messageId,
        sourceId,
        width,
        height,
        settings,
        progressive: false,
      };
      const transfer: Transferable[] = [];
      if (sourceData) {
        payload.sourceBuffer = sourceData.buffer;
        transfer.push(sourceData.buffer);
      }
      worker.postMessage(payload, transfer);
    } catch (error) {
      cleanup();
      reject(error instanceof Error ? error : new Error('Failed to send image data to Filter worker.'));
    }
  });
};

const canvasToObjectUrl = (canvas: HTMLCanvasElement, errorMessage: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error(errorMessage));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
};

const imageDataToObjectUrl = (imageData: ImageData): Promise<string> => {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(new Error('Could not create preview canvas.'));
  }
  context.putImageData(imageData, 0, 0);
  return canvasToObjectUrl(canvas, 'Could not encode Filter preview.');
};

export const useStudioDevelop = ({ kernel }: UseStudioDevelopProps): UseStudioDevelopReturnType => {
  const previewRequestTokenRef = useRef<string | null>(null);
  const lastCompletedPreviewKeyRef = useRef<string | null>(null);
  const settingEditStartRef = useRef<Partial<Record<keyof StudioDevelopSettings, number>>>({});
  const filterBaselineRef = useRef<FilterBaselineState | null>(null);
  const applyRequestTokenRef = useRef<string | null>(null);
  const fullResWorkerRef = useRef<Worker | null>(null);
  const versionSnapshotTimeoutRef = useRef<number | null>(null);

  const patchStudioDevelopSession = useCallback((
    projectId: string,
    workspaceId: string,
    patch: Partial<StudioDevelopSession>
  ) => {
    if (!kernel.current || !projectId) return;
    const current = kernel.current.readProjectRuntimeState(projectId).developSession;
    if (!current || current.workspaceId !== workspaceId) return;
    kernel.current.patchProjectRuntimeState(projectId, {
      developSession: {
        ...current,
        ...patch,
        settings: patch.settings ? normalizeStudioDevelopSettings(patch.settings) : normalizeStudioDevelopSettings(current.settings),
      },
    });
  }, [kernel]);

  const clearPreviewState = useCallback((
    projectId: string,
    workspaceId: string,
    patch: Partial<StudioDevelopSession> = {}
  ) => {
    if (!kernel.current || !projectId) return;
    const current = kernel.current.readProjectRuntimeState(projectId).developSession;
    if (!current || current.workspaceId !== workspaceId) return;
    const nextSettings = patch.settings ? normalizeStudioDevelopSettings(patch.settings) : normalizeStudioDevelopSettings(current.settings);
    const isNoop =
      !current.previewUrl &&
      !current.previewError &&
      current.isPreviewRendering !== true &&
      areStudioDevelopSettingsEqual(normalizeStudioDevelopSettings(current.settings), nextSettings);
    if (isNoop) return;
    revokePreviewUrl(current.previewUrl);
    kernel.current.patchProjectRuntimeState(projectId, {
      developSession: {
        ...current,
        ...patch,
        previewUrl: null,
        previewError: null,
        isPreviewRendering: false,
        settings: nextSettings,
      },
    });
  }, [kernel]);

  const clearScheduledVersionSnapshot = useCallback(() => {
    if (versionSnapshotTimeoutRef.current !== null) {
      window.clearTimeout(versionSnapshotTimeoutRef.current);
      versionSnapshotTimeoutRef.current = null;
    }
  }, []);

  const scheduleFilterVersionSnapshot = useCallback((workspaceId: string) => {
    clearScheduledVersionSnapshot();
    versionSnapshotTimeoutRef.current = window.setTimeout(() => {
      versionSnapshotTimeoutRef.current = null;
      kernel.current?.ensureCurrentStateAsVersion(
        workspaceId,
        'Filters updated',
        { lightweight: true }
      ).catch((error) => {
        console.error('[useStudioDevelop] Failed to snapshot Filter update', error);
      });
    }, 900);
  }, [clearScheduledVersionSnapshot, kernel]);

  const getFullResWorker = useCallback(() => {
    if (typeof Worker === 'undefined') return null;
    if (!fullResWorkerRef.current) {
      fullResWorkerRef.current = createStudioDevelopWorker();
    }
    return fullResWorkerRef.current;
  }, []);

  const terminateFullResWorker = useCallback(() => {
    fullResWorkerRef.current?.terminate();
    fullResWorkerRef.current = null;
    if (filterBaselineRef.current) {
      filterBaselineRef.current.workerSourceCached = false;
    }
  }, []);

  const runFullResolutionFilter = useCallback(async (
    baseline: FilterBaselineState,
    filterSettings: StudioDevelopSettings
  ): Promise<ImageData> => {
    const worker = getFullResWorker();
    if (!worker) {
      return applyStudioDevelopSettingsToImageData(baseline.baseImageData, filterSettings);
    }

    const sourceImageData = baseline.workerSourceCached ? null : baseline.baseImageData;
    try {
      const result = await runCachedStudioDevelopWorker(
        worker,
        baseline.workerSourceId,
        sourceImageData,
        baseline.baseImageData.width,
        baseline.baseImageData.height,
        filterSettings
      );
      baseline.workerSourceCached = true;
      return result;
    } catch (error) {
      console.warn('[useStudioDevelop] Cached Filter worker failed, restarting worker and falling back for this update.', error);
      if (fullResWorkerRef.current === worker) {
        terminateFullResWorker();
      }
      return applyStudioDevelopSettingsToImageData(baseline.baseImageData, filterSettings);
    }
  }, [getFullResWorker, terminateFullResWorker]);

  const clearFilterOverlay = useCallback((
    projectId: string,
    workspaceId: string,
    feedbackSettings: StudioDevelopSettings,
    progress = 1
  ) => {
    const k = kernel.current;
    if (!k || !projectId) return;
    const latestSession = k.readProjectRuntimeState(projectId).developSession;
    if (!latestSession || latestSession.workspaceId !== workspaceId) return;
    revokeSessionUrls(latestSession);
    k.patchProjectRuntimeState(projectId, {
      developSession: {
        ...latestSession,
        settings: normalizeStudioDevelopSettings(feedbackSettings),
        isApplying: false,
        isPreviewRendering: false,
        previewUrl: null,
        applyPreviewUrl: null,
        applyProgress: progress,
        previewError: null,
      },
    });
  }, [kernel]);

  const showBaselinePreviewOverlay = useCallback(async (
    projectId: string,
    workspaceId: string,
    baseline: FilterBaselineState,
    previewSettingsInput: StudioDevelopSettings,
    shouldApply: () => boolean = () => true
  ): Promise<boolean> => {
    const k = kernel.current;
    if (!k || !projectId || !shouldApply()) return false;
    const previewSettings = normalizeStudioDevelopSettings(previewSettingsInput);
    const adjustedPreview = await runStudioDevelopWorker(baseline.previewBaseImageData, previewSettings);
    const nextPreviewUrl = await imageDataToObjectUrl(adjustedPreview);
    if (!shouldApply()) {
      revokePreviewUrl(nextPreviewUrl);
      return false;
    }
    const latest = kernel.current?.readProjectRuntimeState(projectId).developSession;
    if (!latest || latest.workspaceId !== workspaceId) {
      revokePreviewUrl(nextPreviewUrl);
      return false;
    }

    revokeSessionUrls(latest);
    kernel.current?.patchProjectRuntimeState(projectId, {
      developSession: {
        ...latest,
        settings: previewSettings,
        isApplying: false,
        isPreviewRendering: false,
        previewUrl: nextPreviewUrl,
        applyPreviewUrl: null,
        applyProgress: 0,
        previewError: null,
      },
    });
    return true;
  }, [kernel]);

  /**
   * The engine retains exactly one baseline, in a Kernel singleton, but the undo/redo closures that
   * carry one outlive the panel session that pushed them (global undo stack, 10 steps). Before the
   * extraction those closures read and wrote the ref of their own, by then unmounted, component:
   * nothing else could ever be in there. Here the ref can hold another session's baseline, so both
   * sides of the ref are ruled by lineage. `filterLayerId` is minted once per baseline creation and
   * carried verbatim by every later copy, so it identifies the session that produced it.
   *
   * Installs the baseline a live panel session just built for the workspace it is editing.
   */
  const retainFilterBaseline = useCallback((projectId: string, baseline: FilterBaselineState) => {
    const liveSession = kernel.current?.readProjectRuntimeState(projectId).developSession;
    if (!liveSession || liveSession.workspaceId !== baseline.workspaceId) return;
    filterBaselineRef.current = baseline;
  }, [kernel]);

  /**
   * Refreshes the retained baseline after a commit or a revert. Only the lineage currently held may
   * be refreshed: an undo replayed once the panel is closed (ref cleared) or once another session
   * has installed its own baseline must not put its own back in place.
   */
  const updateRetainedFilterBaseline = useCallback((baseline: FilterBaselineState) => {
    if (filterBaselineRef.current?.filterLayerId !== baseline.filterLayerId) return;
    filterBaselineRef.current = baseline;
  }, []);

  /**
   * Read side of the same rule. An undo/redo closure adopts the retained baseline only when it is
   * its own, and otherwise falls back on the one it captured. The fallback is lossless: the only
   * fields that differ between successive copies of one lineage are `committedSettings`,
   * `committedImageData` and `workerSourceCached`, and no undo/redo path reads any of them. They
   * read `filterLayerId`, `workspaceId`, `frameRect`, `previewBaseImageData`, `sourceLayerSnapshots`,
   * `workspaceSnapshotBefore` and `selectedLayerIdsBefore`, all fixed at creation.
   */
  const resolveActionBaseline = useCallback((capturedBaseline: FilterBaselineState): FilterBaselineState => {
    const retained = filterBaselineRef.current;
    return retained?.filterLayerId === capturedBaseline.filterLayerId ? retained : capturedBaseline;
  }, []);

  const restoreFilterBaseline = useCallback((projectId: string, baseline: FilterBaselineState) => {
    const k = kernel.current;
    if (!k) return;
    clearScheduledVersionSnapshot();

    const currentFilterLayer = k.getLayerRegistry()[baseline.filterLayerId];
    if (currentFilterLayer) {
      k.detachLayerFromWorkspace(baseline.workspaceId, baseline.filterLayerId);
      k.setLayer(baseline.filterLayerId, {
        ...currentFilterLayer,
        visible: false,
        layerWorkspaceId: '',
      });
      void k.queueStudioWrite(
        [{
          action: 'UPDATE',
          entity: 'StudioLayer',
          id: baseline.filterLayerId,
          payload: { workspaceId: null },
        }] as any
      ).catch((error) => {
        console.error('[useStudioDevelop] Failed to persist Filter detach', error);
      });
    }

    baseline.sourceLayerSnapshots.forEach((layerSnapshot) => {
      k.setLayer(layerSnapshot.id, cloneLayerSnapshot(layerSnapshot));
    });
    k.setWorkspace(baseline.workspaceId, (workspace) => ({
      ...workspace,
      layerIds: [...baseline.workspaceSnapshotBefore.layerIds],
      versions: (baseline.workspaceSnapshotBefore.versions ?? []).map((version) => ({
        ...version,
        layerVisibility: Array.isArray(version.layerVisibility)
          ? version.layerVisibility.map((entry) => ({ ...entry }))
          : [],
      })),
      activeVersionId: baseline.workspaceSnapshotBefore.activeVersionId ?? null,
      validatedVersionId: baseline.workspaceSnapshotBefore.validatedVersionId ?? null,
    }), projectId);
    setProjectSelectedLayersId(k, projectId, baseline.selectedLayerIdsBefore);
    k.invalidateWorkspaceVersionCache(baseline.workspaceId);
    updateRetainedFilterBaseline({
      ...baseline,
      committedSettings: null,
      committedImageData: null,
    });
  }, [clearScheduledVersionSnapshot, kernel, updateRetainedFilterBaseline]);

  const applyFilterLayerState = useCallback(async (
    projectId: string,
    baseline: FilterBaselineState,
    imageData: ImageData,
    appliedSettings: StudioDevelopSettings
  ) => {
    const k = kernel.current;
    if (!k) return;

    const layerScale = baseline.frameRect.w > 0
      ? baseline.frameRect.w / imageData.width
      : 1;
    const filterLayerPartial = {
      id: baseline.filterLayerId,
      timestamp: Date.now(),
      name: 'Filtered composite',
      rect: {
        x: 0,
        y: 0,
        w: baseline.frameRect.w,
        h: baseline.frameRect.h,
      },
      scale: Number.isFinite(layerScale) && layerScale > 0 ? layerScale : 1,
      locked: true,
      source: 'studio_filter',
      params: buildLayerParams(appliedSettings),
      modificationLog: [{
        timestamp: new Date().toISOString(),
        type: 'MANUAL_FILTER',
        description: 'Applied filters to workspace composite.',
      }],
    } satisfies Partial<Layer>;

    const existingFilterLayer = k.getLayerRegistry()[baseline.filterLayerId];
    if (existingFilterLayer) {
      k.attachLayerToWorkspace(baseline.workspaceId, baseline.filterLayerId);
      k.setLayer(baseline.filterLayerId, {
        ...existingFilterLayer,
        ...filterLayerPartial,
        imageData,
        visible: true,
        layerWorkspaceId: baseline.workspaceId,
        contentVersion: Date.now(),
      });
      void k.queueStudioWrite(
        [{
          action: 'UPDATE',
          entity: 'StudioLayer',
          id: baseline.filterLayerId,
          payload: { workspaceId: baseline.workspaceId },
        }] as any
      ).catch((error) => {
        console.error('[useStudioDevelop] Failed to persist Filter attach', error);
      });
    } else {
      k.addLayerFromImageData(baseline.workspaceId, imageData, filterLayerPartial);
    }

    baseline.sourceLayerSnapshots.forEach((layerSnapshot) => {
      const currentLayer = k.getLayerRegistry()[layerSnapshot.id];
      if (!currentLayer) return;
      k.setLayer(layerSnapshot.id, {
        ...currentLayer,
        visible: false,
      });
    });

    k.selectLayer(projectId, baseline.filterLayerId, false);
    k.invalidateWorkspaceVersionCache(baseline.workspaceId);
    updateRetainedFilterBaseline({
      ...baseline,
      committedSettings: cloneSettings(appliedSettings),
      committedImageData: imageData,
    });
    scheduleFilterVersionSnapshot(baseline.workspaceId);
  }, [kernel, scheduleFilterVersionSnapshot, updateRetainedFilterBaseline]);

  const commitStudioDevelopSettings = useCallback(async (
    projectId: string,
    workspaceId: string,
    nextSettingsInput: StudioDevelopSettings,
    sourceLabel: string
  ) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle || !projectId) return;

    const currentSession = kernelHandle.readProjectRuntimeState(projectId).developSession;
    if (!currentSession || currentSession.workspaceId !== workspaceId) return;

    const targetWorkspace = kernelHandle.getWorkspace(workspaceId);
    if (!targetWorkspace) {
      kernelHandle.handleError('Workspace not found.');
      return;
    }

    const nextSettings = normalizeStudioDevelopSettings(nextSettingsInput);
    const token = `filter-apply-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    applyRequestTokenRef.current = token;
    revokePreviewUrl(currentSession.applyPreviewUrl);
    kernelHandle.patchProjectRuntimeState(projectId, {
      developSession: {
        ...currentSession,
        settings: nextSettings,
        isApplying: false,
        isPreviewRendering: false,
        applyPreviewUrl: null,
        applyProgress: 0,
        previewError: null,
      },
    });

    try {
      let baseline = filterBaselineRef.current;
      if (!baseline || baseline.workspaceId !== workspaceId) {
        const visibleLayerIds = resolveVisibleLayerIds(kernelHandle, targetWorkspace);
        if (visibleLayerIds.length === 0) {
          throw new Error('No visible layers in this workspace.');
        }

        const workspaceBeforeApply = kernelHandle.getWorkspace(workspaceId);
        if (!workspaceBeforeApply) {
          throw new Error('Workspace not found.');
        }
        const workspaceSnapshotBefore = cloneWorkspaceVersionSnapshot(workspaceBeforeApply);
        const selectedLayerIdsBefore = Array.isArray(kernelHandle.readProjectRuntimeState(projectId).selectedLayersId)
          ? [...(kernelHandle.readProjectRuntimeState(projectId).selectedLayersId ?? [])]
          : [];
        const layerRegistryBefore = kernelHandle.getLayerRegistry();
        const directSourceLayerIds = (workspaceBeforeApply.layerIds ?? []).filter((layerId) => {
          const layer = layerRegistryBefore[layerId];
          return layer && layer.visible !== false;
        });
        const sourceLayerSnapshots = directSourceLayerIds
          .map((layerId) => layerRegistryBefore[layerId])
          .filter((layer): layer is Layer => Boolean(layer))
          .map(cloneLayerSnapshot);

        const rendered = await kernelHandle.renderWorkspaceArea(
          workspaceId,
          visibleLayerIds,
          DEVELOP_TARGET_PIXELS,
          false,
          true,
          1,
          targetWorkspace.frameRect,
          'png'
        );

        if (applyRequestTokenRef.current !== token) return;
        if (!rendered.imageData) {
          throw new Error('Could not render the workspace composite.');
        }

        baseline = {
          workspaceId,
          filterLayerId: createFilterLayerId(),
          workerSourceId: `studio-develop-source-${workspaceId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          workerSourceCached: false,
          baseImageData: rendered.imageData,
          previewBaseImageData: createPreviewImageData(rendered.imageData, DEVELOP_PREVIEW_TARGET_PIXELS),
          frameRect: { ...targetWorkspace.frameRect },
          workspaceSnapshotBefore,
          selectedLayerIdsBefore,
          sourceLayerSnapshots,
          committedSettings: null,
          committedImageData: null,
        };
        retainFilterBaseline(projectId, baseline);
      }

      const previousCommittedSettings = baseline.committedSettings
        ? cloneSettings(baseline.committedSettings)
        : cloneSettings(DEFAULT_STUDIO_DEVELOP_SETTINGS);
      const previousCommittedImageData = baseline.committedImageData ?? null;

      const adjustedImageData = await runFullResolutionFilter(baseline, nextSettings);

      if (applyRequestTokenRef.current !== token) return;

      const actionDescription = `Filter ${sourceLabel} change`;
      let initialRedoSettled = false;
      let actionAppliedAtLeastOnce = false;
      kernelHandle.executeAction({
        description: actionDescription,
        redo: async () => {
          if (initialRedoSettled && !actionAppliedAtLeastOnce) return;
          const shouldGuardAgainstStaleCommit = !initialRedoSettled;
          const isCurrentCommit = () => !shouldGuardAgainstStaleCommit || applyRequestTokenRef.current === token;
          if (!isCurrentCommit()) {
            initialRedoSettled = true;
            return;
          }
          const activeBaseline = resolveActionBaseline(baseline);
          const previewShown = await showBaselinePreviewOverlay(projectId, workspaceId, activeBaseline, nextSettings, isCurrentCommit);
          if (!previewShown || !isCurrentCommit()) {
            initialRedoSettled = true;
            return;
          }
          try {
            await applyFilterLayerState(projectId, activeBaseline, adjustedImageData, nextSettings);
          } finally {
            if (isCurrentCommit()) {
              clearFilterOverlay(projectId, workspaceId, nextSettings, 1);
            }
          }
          initialRedoSettled = true;
          actionAppliedAtLeastOnce = true;
          kernel.current?.logUserAction('Workspace Filter Updated', {
            projectId,
            workspaceId,
            layerId: activeBaseline.filterLayerId,
            sourceLabel,
            settings: nextSettings,
          });
        },
        undo: async () => {
          if (!actionAppliedAtLeastOnce) return;
          const activeBaseline = resolveActionBaseline(baseline);
          await showBaselinePreviewOverlay(projectId, workspaceId, activeBaseline, previousCommittedSettings);
          try {
            if (previousCommittedImageData) {
              await applyFilterLayerState(projectId, activeBaseline, previousCommittedImageData, previousCommittedSettings);
            } else {
              restoreFilterBaseline(projectId, activeBaseline);
            }
          } finally {
            clearFilterOverlay(projectId, workspaceId, previousCommittedSettings, previousCommittedImageData ? 1 : 0);
          }
        },
      });
    } catch (error) {
      if (applyRequestTokenRef.current !== token) return;
      console.error('[useStudioDevelop] Failed to apply filters', error);
      kernelHandle.handleError((error as Error)?.message || 'Failed to apply filters.');
      const latest = kernelHandle.readProjectRuntimeState(projectId).developSession;
      if (latest?.workspaceId === workspaceId) {
        revokePreviewUrl(latest.applyPreviewUrl);
        kernelHandle.patchProjectRuntimeState(projectId, {
          developSession: {
            ...latest,
            settings: normalizeStudioDevelopSettings(latest.settings),
            isApplying: false,
            applyPreviewUrl: null,
            applyProgress: 0,
          },
        });
      }
    }
  }, [applyFilterLayerState, clearFilterOverlay, kernel, resolveActionBaseline, restoreFilterBaseline, retainFilterBaseline, runFullResolutionFilter, showBaselinePreviewOverlay]);

  const scheduleStudioDevelopPreview = useCallback((
    projectId: string,
    workspaceId: string,
    settings: StudioDevelopSettings,
    previewInputKey: string
  ): (() => void) => {
    const noop = () => { };
    if (!kernel.current || !projectId) return noop;

    const previewSettings = normalizeStudioDevelopSettings(settings);
    const previewBaseline = filterBaselineRef.current?.workspaceId === workspaceId
      ? filterBaselineRef.current
      : null;
    const token = `develop-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    previewRequestTokenRef.current = token;

    if (previewBaseline?.committedSettings && areStudioDevelopSettingsEqual(previewBaseline.committedSettings, previewSettings)) {
      lastCompletedPreviewKeyRef.current = previewInputKey;
      clearPreviewState(projectId, workspaceId, { settings: previewSettings });
      return noop;
    }

    if (!previewBaseline && areStudioDevelopSettingsDefault(previewSettings)) {
      lastCompletedPreviewKeyRef.current = previewInputKey;
      clearPreviewState(projectId, workspaceId);
      return noop;
    }

    const currentPreviewSession = kernel.current.readProjectRuntimeState(projectId).developSession;
    if (
      lastCompletedPreviewKeyRef.current === previewInputKey &&
      currentPreviewSession?.workspaceId === workspaceId &&
      typeof currentPreviewSession.previewUrl === 'string' &&
      currentPreviewSession.previewUrl.length > 0 &&
      !currentPreviewSession.previewError
    ) {
      return noop;
    }

    const timeoutId = window.setTimeout(async () => {
      const kernelHandle = kernel.current;
      if (!kernelHandle) return;
      const currentSession = kernelHandle.readProjectRuntimeState(projectId).developSession;
      if (!currentSession || currentSession.workspaceId !== workspaceId || previewRequestTokenRef.current !== token) return;

      const targetWorkspace = kernelHandle.getWorkspace(workspaceId);
      if (!targetWorkspace) return;
      const baselineForPreview = filterBaselineRef.current?.workspaceId === workspaceId
        ? filterBaselineRef.current
        : null;
      if (baselineForPreview?.committedSettings && areStudioDevelopSettingsEqual(baselineForPreview.committedSettings, previewSettings)) {
        lastCompletedPreviewKeyRef.current = previewInputKey;
        clearPreviewState(projectId, workspaceId, { settings: previewSettings });
        return;
      }

      const visibleLayerIds = baselineForPreview ? [] : resolveVisibleLayerIds(kernelHandle, targetWorkspace);
      if (!baselineForPreview && visibleLayerIds.length === 0) {
        lastCompletedPreviewKeyRef.current = previewInputKey;
        clearPreviewState(projectId, workspaceId);
        return;
      }

      kernelHandle.patchProjectRuntimeState(projectId, {
        developSession: {
          ...currentSession,
          isPreviewRendering: true,
          previewError: null,
        },
      });

      try {
        let previewSourceImageData = baselineForPreview?.previewBaseImageData ?? null;
        if (!previewSourceImageData) {
          const rendered = await kernelHandle.renderWorkspaceArea(
            workspaceId,
            visibleLayerIds,
            DEVELOP_PREVIEW_TARGET_PIXELS,
            false,
            true,
            1,
            targetWorkspace.frameRect,
            'png'
          );

          if (!rendered.imageData) {
            throw new Error('Could not render the Filter preview.');
          }
          previewSourceImageData = rendered.imageData;
        }

        const adjustedPreview = await runStudioDevelopWorker(previewSourceImageData, previewSettings);
        const nextPreviewUrl = await imageDataToObjectUrl(adjustedPreview);
        if (previewRequestTokenRef.current !== token) {
          revokePreviewUrl(nextPreviewUrl);
          return;
        }

        const latest = kernelHandle.readProjectRuntimeState(projectId).developSession;
        if (!latest || latest.workspaceId !== workspaceId) {
          revokePreviewUrl(nextPreviewUrl);
          return;
        }
        revokePreviewUrl(latest.previewUrl);
        lastCompletedPreviewKeyRef.current = previewInputKey;
        kernelHandle.patchProjectRuntimeState(projectId, {
          developSession: {
            ...latest,
            previewUrl: nextPreviewUrl,
            previewError: null,
            isPreviewRendering: false,
          },
        });
      } catch (error) {
        if (previewRequestTokenRef.current !== token) return;
        console.error('[useStudioDevelop] Failed to render Filter preview', error);
        const latest = kernelHandle.readProjectRuntimeState(projectId).developSession;
        if (!latest || latest.workspaceId !== workspaceId) return;
        lastCompletedPreviewKeyRef.current = previewInputKey;
        kernelHandle.patchProjectRuntimeState(projectId, {
          developSession: {
            ...latest,
            previewError: (error as Error)?.message || 'Failed to render Filter preview.',
            isPreviewRendering: false,
          },
        });
      }
    }, DEVELOP_PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clearPreviewState, kernel]);

  const beginStudioDevelopSettingEdit = useCallback((
    projectId: string,
    workspaceId: string,
    key: keyof StudioDevelopSettings
  ) => {
    const currentSession = kernel.current?.readProjectRuntimeState(projectId).developSession;
    if (!currentSession || currentSession.workspaceId !== workspaceId) return;
    settingEditStartRef.current[key] = normalizeStudioDevelopSettings(currentSession.settings)[key];
  }, [kernel]);

  const changeStudioDevelopSetting = useCallback((
    projectId: string,
    workspaceId: string,
    key: keyof StudioDevelopSettings,
    value: number
  ) => {
    const currentSession = kernel.current?.readProjectRuntimeState(projectId).developSession;
    if (!currentSession || currentSession.workspaceId !== workspaceId) return;
    patchStudioDevelopSession(projectId, workspaceId, {
      settings: {
        ...normalizeStudioDevelopSettings(currentSession.settings),
        [key]: value,
      },
      previewError: null,
    });
  }, [kernel, patchStudioDevelopSession]);

  const commitStudioDevelopSetting = useCallback((
    projectId: string,
    workspaceId: string,
    key: keyof StudioDevelopSettings,
    value: number
  ) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle || !projectId) return;

    const startValue = settingEditStartRef.current[key];
    delete settingEditStartRef.current[key];
    if (typeof startValue !== 'number' || !Number.isFinite(startValue)) return;

    const currentSession = kernelHandle.readProjectRuntimeState(projectId).developSession;
    if (!currentSession || currentSession.workspaceId !== workspaceId) return;

    const finalSettings = normalizeStudioDevelopSettings({
      ...normalizeStudioDevelopSettings(currentSession.settings),
      [key]: value,
    });
    const finalValue = finalSettings[key];
    if (Math.abs(startValue - finalValue) < 0.0001) return;
    void commitStudioDevelopSettings(projectId, workspaceId, finalSettings, getControlLabel(key));
  }, [commitStudioDevelopSettings, kernel]);

  const revertStudioDevelopFilters = useCallback((projectId: string, workspaceId: string) => {
    const k = kernel.current;
    if (!k || !projectId) return;

    const baseline = filterBaselineRef.current;
    if (!baseline || baseline.workspaceId !== workspaceId || !baseline.committedImageData || !baseline.committedSettings) {
      applyRequestTokenRef.current = null;
      previewRequestTokenRef.current = null;
      patchStudioDevelopSession(projectId, workspaceId, { settings: cloneSettings(DEFAULT_STUDIO_DEVELOP_SETTINGS), previewUrl: null, applyPreviewUrl: null, applyProgress: 0 });
      k.logUserAction('Workspace Filter Settings Reset', {
        projectId,
        workspaceId,
      });
      return;
    }

    const restoredImageData = baseline.committedImageData;
    const restoredSettings = cloneSettings(baseline.committedSettings);
    k.executeAction({
      description: 'Revert all filters',
      redo: async () => {
        applyRequestTokenRef.current = null;
        previewRequestTokenRef.current = null;
        const defaultSettings = cloneSettings(DEFAULT_STUDIO_DEVELOP_SETTINGS);
        await showBaselinePreviewOverlay(projectId, workspaceId, baseline, defaultSettings);
        try {
          restoreFilterBaseline(projectId, baseline);
        } finally {
          clearFilterOverlay(projectId, workspaceId, defaultSettings, 0);
        }
        kernel.current?.logUserAction('Workspace Filter Reverted', {
          projectId,
          workspaceId,
          layerId: baseline.filterLayerId,
        });
      },
      undo: async () => {
        await showBaselinePreviewOverlay(projectId, workspaceId, baseline, restoredSettings);
        try {
          await applyFilterLayerState(projectId, baseline, restoredImageData, restoredSettings);
        } finally {
          clearFilterOverlay(projectId, workspaceId, restoredSettings, 1);
        }
      },
    });
  }, [applyFilterLayerState, clearFilterOverlay, kernel, patchStudioDevelopSession, restoreFilterBaseline, showBaselinePreviewOverlay]);

  const closeStudioDevelopSession = useCallback((projectId: string, workspaceId: string) => {
    if (!kernel.current || !projectId) return;
    const current = kernel.current.readProjectRuntimeState(projectId).developSession;
    if (current?.workspaceId === workspaceId) {
      applyRequestTokenRef.current = null;
      previewRequestTokenRef.current = null;
      revokeSessionUrls(current);
    }
    kernel.current.patchProjectRuntimeState(projectId, { developSession: null });
    kernel.current.logUserAction('Workspace Filter Panel Closed', {
      projectId,
      workspaceId,
    });
  }, [kernel]);

  const teardownStudioDevelop = useCallback((projectId: string, workspaceId: string) => {
    clearScheduledVersionSnapshot();
    terminateFullResWorker();
    const current = kernel.current?.readProjectRuntimeState(projectId).developSession;
    if (current?.workspaceId === workspaceId) {
      applyRequestTokenRef.current = null;
      previewRequestTokenRef.current = null;
      revokeSessionUrls(current);
    }
    // This engine state used to live in the panel's own refs, so it died with the panel. The
    // Kernel module is a singleton: without an explicit release, a reopened panel inherits the
    // previous session's baseline (stale composite painted as an overlay, undo pointing at a dead
    // session, and tens of MB of ImageData retained).
    if (shouldReleaseStudioDevelopBaseline(filterBaselineRef.current?.workspaceId, workspaceId)) {
      filterBaselineRef.current = null;
    }
    lastCompletedPreviewKeyRef.current = null;
    settingEditStartRef.current = {};
  }, [clearScheduledVersionSnapshot, kernel, terminateFullResWorker]);

  return {
    beginStudioDevelopSettingEdit,
    changeStudioDevelopSetting,
    commitStudioDevelopSetting,
    commitStudioDevelopSettings,
    scheduleStudioDevelopPreview,
    revertStudioDevelopFilters,
    closeStudioDevelopSession,
    teardownStudioDevelop,
  };
};
